import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authApi } from "../../services/api";
import logo from "../../images/developer_02.png";
import "../../css/admin.css";

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [registrationAllowed, setRegistrationAllowed] = useState(false);

  useEffect(() => {
    const checkCanRegister = async () => {
      try {
        setRegistrationAllowed(true);
        return;
        const response = await authApi.canRegister();
        if (response.success && response.data) {
          setRegistrationAllowed(
            (response.data as Record<string, unknown>).allowed !== false
          );
        } else {
          setRegistrationAllowed(false);
        }
      } catch {
        setRegistrationAllowed(false);
      } finally {
        setChecking(false);
      }
    };

    void checkCanRegister();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const newFieldErrors: Record<string, string> = {};
    if (!email.trim()) newFieldErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newFieldErrors.email = "Enter a valid email";
    if (!password) newFieldErrors.password = "Password is required";
    else if (password.length < 6) newFieldErrors.password = "Password must be at least 6 characters";
    if (!confirmPassword) newFieldErrors.confirmPassword = "Confirm your password";
    else if (password !== confirmPassword) newFieldErrors.confirmPassword = "Passwords do not match";
    setFieldErrors(newFieldErrors);
    if (Object.keys(newFieldErrors).length > 0) return;

    setLoading(true);

    try {
      const response = await authApi.register({ email, password });

      if (response.success) {
        navigate("/admin/login", { state: { registered: true } });
      } else {
        setError(response.message || "Registration failed.");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-login-logo">
            <img src={logo} alt="Logo" />
          </div>
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "2rem" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!registrationAllowed) {
    return (
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-login-logo">
            <img src={logo} alt="Logo" />
            <h1>Registration Unavailable</h1>
            <p>New account registration is currently disabled.</p>
          </div>
          <p className="admin-register-link">
            Already have an account?{" "}
            <Link to="/admin/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-wrapper">
      <div className="admin-login-card">
        <div className="admin-login-logo">
          <img src={logo} alt="Logo" />
          <h1>Create Account</h1>
          <p>Register to access the admin panel</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label">Email</label>
            <input
              type="email"
              className={`admin-form-input${fieldErrors.email ? " admin-input-error" : ""}`}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => ({ ...prev, email: "" })); }}
              autoFocus
            />
            {fieldErrors.email && <span className="admin-field-error">{fieldErrors.email}</span>}
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Password</label>
            <input
              type="password"
              className={`admin-form-input${fieldErrors.password ? " admin-input-error" : ""}`}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setFieldErrors((prev) => ({ ...prev, password: "" })); }}
            />
            {fieldErrors.password && <span className="admin-field-error">{fieldErrors.password}</span>}
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Confirm Password</label>
            <input
              type="password"
              className={`admin-form-input${fieldErrors.confirmPassword ? " admin-input-error" : ""}`}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors((prev) => ({ ...prev, confirmPassword: "" })); }}
            />
            {fieldErrors.confirmPassword && <span className="admin-field-error">{fieldErrors.confirmPassword}</span>}
          </div>

          {error && <p className="admin-form-error">{error}</p>}

          <button
            type="submit"
            className="admin-btn admin-btn-primary admin-btn-full"
            disabled={loading}
            style={{ marginTop: "1.5rem" }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-user-plus" />
                <span>Register</span>
              </>
            )}
          </button>

          <p className="admin-register-link">
            Already have an account?{" "}
            <Link to="/admin/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
